import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/auth/use-auth';
import { QUERY_KEYS } from '@/config/queries';
import { QUERY_CONFIG } from '@/config/constants';

const actionSchema = z.object({
  id: z.string().uuid(),
  nudge_id: z.string().min(1),
  pillar: z.enum(['vitality', 'resilience', 'discipline']),
  shown_at: z.string().datetime({ offset: true }),
  shown_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  completion_points: z.number().int().nonnegative(),
  action_snapshot: z.string().nullable(),
  completion_revision: z.number().int().nonnegative(),
  action_type: z.enum(['warning', 'insight', 'achievement']).nullable(),
  award_points: z.number().int().positive().nullable(),
});
export type HealthAction = z.infer<typeof actionSchema>;

const mutationSchema = z.object({
  status: z.enum([
    'applied',
    'unchanged',
    'stale',
    'legacy_unreconciled',
    'insufficient_points',
    'unavailable',
  ]),
  id: z.string().uuid(),
  shown_date: z.string(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  completion_revision: z.number().int().nonnegative(),
  completion_points: z.number().int().nonnegative(),
  points_awarded: z.number().int().nonnegative(),
  points_removed: z.number().int().nonnegative(),
});

export function useHealthActions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const queryKey = QUERY_KEYS.HEALTH_ACTIONS(userId ?? '');
  const {
    data: actions = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<HealthAction[]> => {
      const { data, error } = await supabase.rpc('get_active_health_actions', {
        p_user_id: userId!,
      });
      if (error) throw error;
      return z
        .array(actionSchema)
        .max(10)
        .parse(data ?? []);
    },
    enabled: !!userId,
    staleTime: QUERY_CONFIG.STALE_TIME.SHORT,
    retry: 2,
  });

  const toggleAction = useMutation({
    mutationFn: async ({ action }: { action: HealthAction }) => {
      if (!userId) throw new Error('Authentication required');
      const { data, error } = await supabase.rpc('set_health_action_completed', {
        p_action_id: action.id,
        p_completed: action.completed_at === null,
        p_expected_revision: action.completion_revision,
      });
      if (error) throw error;
      const result = mutationSchema.parse(data);
      if (result.id !== action.id || result.shown_date !== action.shown_date) {
        throw new Error('Invalid action response');
      }
      if (
        result.status !== 'applied' &&
        result.status !== 'unchanged' &&
        result.status !== 'stale'
      ) {
        throw new Error('Action could not be saved');
      }
      return result;
    },
    onMutate: async ({ action }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient
        .getQueryData<HealthAction[]>(queryKey)
        ?.find((row) => row.id === action.id && row.shown_date === action.shown_date);
      queryClient.setQueryData<HealthAction[]>(queryKey, (old = []) =>
        old.map((row) =>
          row.id === action.id && row.shown_date === action.shown_date
            ? { ...row, completed_at: action.completed_at ? null : new Date().toISOString() }
            : row,
        ),
      );
      return { previous };
    },
    onSuccess: (result) => {
      queryClient.setQueryData<HealthAction[]>(queryKey, (old = []) =>
        old.map((row) =>
          row.id === result.id &&
          row.shown_date === result.shown_date &&
          result.completion_revision >= row.completion_revision
            ? {
                ...row,
                completed_at: result.completed_at,
                completion_revision: result.completion_revision,
                completion_points: result.completion_points,
              }
            : row,
        ),
      );
    },
    onError: (_err, { action }, context) => {
      const previous = context?.previous;
      if (previous) {
        queryClient.setQueryData<HealthAction[]>(queryKey, (old = []) =>
          old.map((row) =>
            row.id === action.id &&
            row.shown_date === action.shown_date &&
            row.completion_revision === action.completion_revision
              ? previous
              : row,
          ),
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POINTS(userId ?? '') });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TOTAL_POINTS(userId ?? '') });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.REWARDS_DATA(userId ?? '') });
      for (const range of ['7d', '30d', '90d']) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.HISTORICAL_POINTS(userId ?? '', range),
        });
      }
    },
  });

  const completedCount = actions.filter((action) => action.completed_at !== null).length;
  const totalCount = actions.length;
  return {
    actions,
    completedCount,
    totalCount,
    allComplete: totalCount > 0 && completedCount === totalCount,
    isLoading,
    error,
    refetch,
    toggleAction,
  };
}
